import { useEffect, useId, type ReactNode, type RefObject } from 'react';
import { getEloLevel } from '../../engine';
import { DEFAULT_LOCALE, translate, useT, type LocaleContextValue } from '../../i18n';
import { Dialog } from '../ui/Dialog';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// Small shared building blocks for the mobile app screens. Kept deliberately
// tiny: the screens should read as UI, not as a component library.
// ─────────────────────────────────────────────────────────────────────

/**
 * The translator, defaulted to the SOURCE catalog (#310).
 *
 * `nameOf` and its two derivatives are plain functions, not components, so
 * they cannot call `useT()`. The default keeps every existing call site
 * unchanged and takes the word from the catalog rather than from a literal
 * typed here; a caller that has a `t` passes it and the name follows the
 * reader's language.
 */
type Translate = LocaleContextValue['t'];
const sourceCatalog: Translate = (key, vars) => translate(DEFAULT_LOCALE, key, vars);

export const nameOf = (db: any[] = [], id: any, t: Translate = sourceCatalog): string =>
  (db || []).find((p: any) => String(p.id) === String(id))?.name || t('mobile.common.player');

export const firstNameOf = (db: any[] = [], id: any, t: Translate = sourceCatalog): string =>
  nameOf(db, id, t).split(' ')[0];

/** Family name = last whitespace-separated token (the same rule as the KM draw sheet). */
export const lastNameOf = (db: any[] = [], id: any, t: Translate = sourceCatalog): string => {
  const n = nameOf(db, id, t).trim();
  return n.split(/\s+/).slice(-1)[0] || n;
};

/**
 * One player in a match line-up: surname, full name on hover, and «me»
 * highlighted in lime so you spot yourself at a glance. `null` = open slot.
 */
export function PlayerName({
  db,
  id,
  meId,
  fallback,
}: {
  db: any[];
  id: any;
  meId?: any;
  /** Overrides «ledig» for an empty slot. Nothing passes one today. */
  fallback?: string;
}) {
  const t = useT();
  if (id == null)
    return (
      <span className="text-ink-3 font-normal">{fallback ?? t('mobile.common.openSlot')}</span>
    );
  const me = meId != null && String(id) === String(meId);
  return (
    <span
      title={nameOf(db, id, t)}
      className={me ? 'rounded px-1 font-bold' : ''}
      style={me ? { background: '#c2f02f', color: '#0f1611' } : undefined}
    >
      {lastNameOf(db, id, t)}
    </span>
  );
}

/** A side of a match: «Aas/Bakken» — Padel24 slots (with placeholder labels) or flat ids. */
export function TeamNames({ team, db, meId }: { team: any; db: any[]; meId?: any }) {
  const ids = [team?.p1?.playerId ?? team?.p1Id ?? null, team?.p2?.playerId ?? team?.p2Id ?? null];
  if (ids.every((x) => x == null)) {
    const labels = [team?.p1?.label, team?.p2?.label].filter(Boolean);
    if (labels.length) return <>{labels.join('/')}</>;
  }
  return (
    <>
      <PlayerName db={db} id={ids[0]} meId={meId} />
      <span className="text-ink-3">/</span>
      <PlayerName db={db} id={ids[1]} meId={meId} />
    </>
  );
}

export const initials = (name: string = ''): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

/** Level chip: «N4 · Middels» in the level's own colour. */
export function LevelChip({ elo, compact = false }: { elo: number; compact?: boolean }) {
  const lvl = getEloLevel(Math.round(elo || 0));
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full font-mono font-bold"
      style={{
        background: lvl.color,
        color: lvl.text,
        fontSize: compact ? 10 : 11,
        padding: compact ? '2px 8px' : '3px 10px',
        letterSpacing: '0.04em',
      }}
      title={lvl.description}
    >
      N{lvl.level}
      {!compact && <span className="font-body font-semibold">· {lvl.name}</span>}
    </span>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between px-1 mb-2 mt-1">
      <h2 className="font-display font-semibold text-ink" style={{ fontSize: 17 }}>
        {children}
      </h2>
      {action}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
}) {
  return (
    <div
      className="rounded-2xl px-4 py-6 text-center"
      style={{ background: '#fff', border: '1px dashed #e7eae3' }}
    >
      {icon && (
        <div className="flex justify-center mb-2" style={{ color: 'var(--ink-3)' }}>
          {icon}
        </div>
      )}
      <div className="text-[13px] font-semibold text-ink-2">{title}</div>
      {hint && <div className="text-[12px] text-ink-3 mt-0.5">{hint}</div>}
    </div>
  );
}

/** Rounded white card, the app's basic surface. */
export function Card({
  children,
  onClick,
  accent,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  accent?: string;
  className?: string;
}) {
  const Tag: any = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`block w-full text-left rounded-2xl px-4 py-3.5 ${onClick ? 'active:scale-[0.99] transition-transform' : ''} ${className}`}
      style={{
        background: '#fff',
        border: '1px solid #ecefe8',
        boxShadow: '0 1px 2px rgba(15,22,17,0.04)',
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
    >
      {children}
    </Tag>
  );
}

/** Big primary/secondary action buttons used across screens. */
export function BigButton({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  'aria-describedby': describedBy,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'lime' | 'ghost' | 'danger';
  disabled?: boolean;
  /** The element that says why it is disabled, read with the button (#1302). */
  'aria-describedby'?: string;
}) {
  const styles: Record<string, any> = {
    primary: { background: '#0f1611', color: '#fff' },
    lime: { background: '#c2f02f', color: '#0f1611' },
    ghost: { background: '#fff', color: '#0f1611', border: '1px solid var(--line-strong)' },
    danger: { background: '#ffe4e6', color: '#be123c' },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-describedby={describedBy}
      className="w-full rounded-2xl font-semibold text-[14px] py-3.5 transition-transform active:scale-[0.98] disabled:opacity-40"
      style={styles[variant]}
    >
      {children}
    </button>
  );
}

/** A segmented control (pill tabs) — e.g. Kommende · Spilt · Turneringer. */
export function Segments<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string; count?: number }[];
}) {
  return (
    // A pill row keeps its phone proportions on a wide screen (desktop step 4)
    // rather than stretching two labels across the page.
    <div
      className="flex p-1 rounded-2xl mb-3 @wide:max-w-[420px]"
      style={{ background: '#eef0ea' }}
    >
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            // WHICH ONE IS ACTIVE WAS A BACKGROUND COLOUR AND NOTHING ELSE —
            // invisible to assistive technology and to anyone who cannot tell
            // #fff from #eef0ea (WCAG 1.4.1 / 4.1.2). `aria-pressed` rather
            // than a radiogroup: these are three toggle buttons that each stand
            // on their own, and a radiogroup would owe the arrow-key
            // navigation that a pill row does not have.
            aria-pressed={active}
            className="flex-1 rounded-xl py-1.5 text-[12px] font-semibold transition-colors"
            style={
              active
                ? { background: '#fff', color: '#0f1611', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
                : { color: '#4a544c' }
            }
          >
            {o.label}
            {o.count != null && o.count > 0 && (
              <span
                className="ml-1 font-mono text-[10px] px-1.5 rounded-full"
                style={{ background: active ? '#c2f02f' : '#dfe3da', color: '#0f1611' }}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Close a popover with Escape (desktop brief: keyboard use).
 *
 * The SHEETS no longer use this: they are `MobileSheet`, and its `Dialog`
 * handles Escape along with focus. The one user left is the «Administrasjon»
 * flyout. It is a `role="menu"`, not a dialog, so it needs Escape but must not
 * trap focus or lock the page. Pass `null` while it should not listen.
 */
export function useEscapeToClose(onClose: (() => void) | null): void {
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

// ─────────────────────────────────────────────────────────────────────
// THE PLAYER APP'S SHEET, BUILT ON `Dialog` (#900).
//
// Five sheets in this app were written out by hand with the same markup. Each
// had a scrim that closed on click and carried `role="dialog"` itself, so the
// element declared modal included the surface you dismiss it with. The panel
// is a bottom sheet on a phone and a centred dialog from the rail width up.
// Beyond that, each one did a different subset of the rest. One moved focus
// in, none trapped it, none gave it back, one had no Escape at all, and Tab
// walked the screen behind every one of them.
//
// This is that markup, written once on top of `Dialog`, so every sheet gets
// the whole set: the role and name on the PANEL, focus moved in on open and
// kept inside, Escape, and focus returned to whatever opened it.
//
// ── NOTHING VISIBLE MOVES ────────────────────────────────────────────
//
// The classes are the ones the hand-written sheets used. The two inline colours
// and the max height became arbitrary-value classes, because `Dialog` takes
// classes, and `bg-[#f7f8f5]` compiles to the same declaration the `style` set.
// The header stays at each call site because the five do not share one: two
// have no ✕, one has no header row at all, and their margins and type sizes
// differ. That is also why the name comes from `labelledBy` and not `heading`.
// The sheet's own `<h2>` IS the title, and `Dialog`'s heading would add a
// second one, styled differently.
//
// ── `absolute`, NOT `fixed` ──────────────────────────────────────────
//
// The scrim covers the APP, not the viewport. The same app renders inside the
// portal's 390 px phone-frame preview, where a viewport overlay would dim the
// portal around the phone. `Dialog` renders in place with no portal, so the
// containing block is still the app's.
// ─────────────────────────────────────────────────────────────────────

const STAY_OPEN = () => {};

/**
 * Ids for a sheet's title and lead, for `MobileSheet`'s `labelledBy` and
 * `describedBy`. `useId()` output contains colons; `safeId` keeps the id usable
 * as a selector too, as `Dialog` and `Field` do.
 */
export function useSheetIds(): { title: string; lead: string } {
  const auto = useId();
  return { title: safeId('sheet-title', auto), lead: safeId('sheet-lead', auto) };
}

export interface MobileSheetProps {
  /** Id of the sheet's own title, which becomes its accessible name. See `useSheetIds`. */
  labelledBy: string;
  /** Id of the lead line under the title, read after the name. */
  describedBy?: string;
  /**
   * Dismiss: Escape and the scrim call it. `null` for a sheet that must be
   * answered (the forced first-launch picker), where neither dismisses. Focus
   * is still kept inside it, because Tab should not wander out of a sheet you
   * cannot leave.
   */
  onClose: (() => void) | null;
  /** Where focus lands on open, such as a search box. Defaults to the first control. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Panel classes on top of the shared shape: bottom padding, layout, max height. */
  className?: string;
  children: ReactNode;
}

/**
 * A bottom sheet on a phone, a centred dialog from `@rail` up, and a modal
 * `Dialog` at every width. Mount it only while it is open.
 */
export function MobileSheet({
  labelledBy,
  describedBy,
  onClose,
  initialFocus,
  className = '',
  children,
}: MobileSheetProps) {
  return (
    <Dialog
      labelledBy={labelledBy}
      describedBy={describedBy}
      // A forced sheet hands `Dialog` a close that does nothing. That covers
      // Escape AND the scrim, so there is no separate backdrop flag to keep in
      // step with it.
      onClose={onClose ?? STAY_OPEN}
      // Each sheet draws its own ✕ in its own header (see above).
      showClose={false}
      initialFocus={initialFocus}
      backdropClassName="absolute inset-0 z-30 flex flex-col justify-end @rail:justify-center @rail:items-center @rail:p-6 bg-[rgba(15,22,17,0.45)]"
      // FITS THE FRAME (#1108). A sheet that sets its own `max-h-…` (the pickers,
      // whose list scrolls inside a flex column) keeps it; any other is capped
      // to the scrim and scrolls as a whole. In percent, not `vh`: the scrim is
      // the phone frame, which is not the window from `@rail` up.
      className={`rounded-t-3xl px-4 pt-3 @rail:rounded-3xl @rail:w-full @rail:max-w-[480px] @rail:px-5 @rail:pt-4 bg-[#f7f8f5] ${/\bmax-h-/.test(className) ? '' : 'max-h-[90%] overflow-y-auto '}${className}`}
    >
      <div
        className="mx-auto rounded-full @rail:hidden"
        style={{ width: 36, height: 4, background: '#dfe3da' }}
      />
      {children}
    </Dialog>
  );
}
